import { LogLevel } from './log-level'
import { Globals } from '../globals'
import { PNException } from '../exceptions/pn-exception.ts'
import { tryEnumFromValue } from '../utils/enums.ts'

type ConsoleMethod = 'info' | 'warn' | 'error' | 'debug' | 'trace'

type LogLevelValue = {
    intValue: number
    label: string
    logMethod: ConsoleMethod
}

const logLevelValues: Record<LogLevel, LogLevelValue> = {
    [LogLevel.VERBOSE]: {
        intValue: 0,
        label: 'Verbose',
        logMethod: 'debug',
    },
    [LogLevel.DEBUG]: {
        intValue: 1,
        label: 'Debug',
        logMethod: 'debug',
    },
    [LogLevel.INFO]: {
        intValue: 2,
        label: 'Info',
        logMethod: 'info',
    },
    [LogLevel.WARN]: {
        intValue: 3,
        label: 'Warn',
        logMethod: 'warn',
    },
    [LogLevel.ERROR]: {
        intValue: 4,
        label: 'Error',
        logMethod: 'error',
    },
    [LogLevel.CRITICAL]: {
        intValue: 5,
        label: 'Critical',
        logMethod: 'error',
    },
    [LogLevel.NONE]: {
        intValue: 6,
        label: 'None',
        logMethod: 'info',
    },
}

const logLevelCacheKey = 'pn_ll'

const globalLogList: [Date, ...any][] = []

const loggerInstances: Record<string, CoreLogger> = {}
const taggedLoggerInstances: Record<string, TaggedLogger> = {}

class CoreLogger {
    public static _getInstance(name?: string): CoreLogger {
        name ||= Globals._publicNamespace

        if (!loggerInstances[name]) {
            loggerInstances[name] = new CoreLogger(name)
        }

        return loggerInstances[name]
    }

    private constructor(public readonly name: string) {
        this._logLevel = LogLevel.NONE
        this._restorePrevLogLevel()
    }

    // region LogLevel
    private _logLevel: LogLevel

    public get logLevel(): LogLevel {
        return this._logLevel
    }

    public set logLevel(level: LogLevel) {
        this._logLevel = level
        this.persistLogLevel(level)
    }

    private _restorePrevLogLevel() {
        if ('localStorage' in self) {
            try {
                const prevLogLevel = self.localStorage.getItem(logLevelCacheKey)
                if (!prevLogLevel || !tryEnumFromValue(LogLevel, prevLogLevel)) {
                    this._log('Logger', LogLevel.VERBOSE, [
                        'Previous log level not found - setting default',
                        this.logLevel,
                    ])
                    this.persistLogLevel(this.logLevel)
                } else {
                    this._log('Logger', LogLevel.VERBOSE, ['Restoring previous log level', this.logLevel])
                    this.logLevel = prevLogLevel as LogLevel
                }
            } catch (error) {
                this._log('Logger', LogLevel.VERBOSE, ['Unable to restore previous log level', error])
            }
        } else {
            this._log('Logger', LogLevel.VERBOSE, ['Skipping log level restore in worker global scope'])
            return
        }
    }

    private persistLogLevel(level: LogLevel) {
        if ('localStorage' in self) {
            try {
                self.localStorage.setItem(logLevelCacheKey, level)
            } catch {}
        }
    }
    // endregion

    public _log(tag: string | null, level: LogLevel, logs: any[]) {
        const reqLevelValue = logLevelValues[level]
        const currLevelValue = logLevelValues[this.logLevel]

        if (tag) {
            logs = logs.slice() // clone
            logs.unshift(`[${tag}]`)
        }

        let logLevelQualifier = reqLevelValue.label[0]
        if (level === LogLevel.CRITICAL) {
            logLevelQualifier = '!' + logLevelQualifier
        }

        // logs should always be stored regardless of if they are printed
        globalLogList.push([new Date(), logLevelQualifier, ...logs])

        if (
            this.logLevel != LogLevel.NONE &&
            level != LogLevel.NONE &&
            reqLevelValue.intValue >= currLevelValue.intValue
        ) {
            try {
                if (
                    'console' in self &&
                    typeof console === 'object' &&
                    reqLevelValue.logMethod in console &&
                    typeof console[reqLevelValue.logMethod] === 'function'
                ) {
                    console[reqLevelValue.logMethod](`[${this.name}: ${logLevelQualifier}]`, ...logs)
                }
            } catch (err) {
                globalLogList.push([new Date(), `!${logLevelValues[LogLevel.CRITICAL].label[0]}`, err])
            }
        }
    }
}

/** @internal */
export type Logger = {
    // @ts-ignore - merged declaration clash
    logLevel: LogLevel
    _getLogs(condensed?: boolean): any[]
    verbose(...logs: any[]): void
    debug(...logs: any[]): void
    info(...logs: any[]): void
    warn(...logs: any[]): void
    error(...logs: any[]): void
    critical(...logs: any[]): void
    _log(level: LogLevel, ...logs: any[]): void
}

/** @internal */
export class TaggedLogger implements Logger {
    public static _getInstance(tag: string | undefined, loggerName: string): Logger
    public static _getInstance(tag: string): Logger
    public static _getInstance(): Logger
    public static _getInstance(tag?: string, loggerName?: string): Logger {
        const tagId = tag ?? 'root'

        if (!loggerInstances[tagId]) {
            taggedLoggerInstances[tagId] = new TaggedLogger(tag, loggerName)
        }

        return taggedLoggerInstances[tagId]
    }

    protected _globalLogger: CoreLogger
    protected _tag: string | null

    private constructor(tag?: string, loggerName?: string) {
        this._globalLogger = CoreLogger._getInstance(loggerName)
        this._tag = tag ?? null
    }

    // @ts-ignore - merged declaration clash
    public get logLevel(): LogLevel {
        return this._globalLogger.logLevel
    }

    // @ts-ignore - merged declaration clash
    public set logLevel(level: LogLevel) {
        this._globalLogger.logLevel = level
    }

    public _getLogs(condensed: boolean = false): any[] {
        return !condensed
            ? globalLogList.map(([, ...entry]) => entry)
            : globalLogList.map(([ts, logLevel, ...log]) =>
                  [`${ts.toISOString()} [${this._globalLogger.name}: ${logLevel}]`, ...log]
                      .map((logValue) => {
                          if (logValue && !Array.isArray(logValue) && typeof logValue === 'object') {
                              try {
                                  return logValue instanceof PNException
                                      ? logValue._compositeMessage
                                      : logValue instanceof Error
                                      ? logValue.message
                                      : JSON.stringify(logValue)
                              } catch {}
                          }
                          return logValue
                      })
                      .join(' '),
              )
    }

    public verbose(...logs: any[]) {
        this._log(LogLevel.VERBOSE, ...logs)
    }
    public debug(...logs: any[]) {
        this._log(LogLevel.DEBUG, ...logs)
    }
    public info(...logs: any[]) {
        this._log(LogLevel.INFO, ...logs)
    }
    public warn(...logs: any[]) {
        this._log(LogLevel.WARN, ...logs)
    }
    public error(...logs: any[]) {
        this._log(LogLevel.ERROR, ...logs)
    }
    public critical(...logs: any[]) {
        this._log(LogLevel.CRITICAL, ...logs)
    }

    public _log(level: LogLevel, ...logs: any[]) {
        this._globalLogger._log(this._tag, level, logs)
    }
}
